import React, { useState } from 'react';
import { GripVertical, ChevronLeft, ChevronRight, Trash2, Film } from 'lucide-react';
import { VideoClipSegment, VideoSource, SelectedMediaTarget } from './types';

interface VideoTrackLaneProps {
  segments: VideoClipSegment[];
  sources: VideoSource[];
  totalSequenceDuration: number;
  selectedMedia: SelectedMediaTarget;
  onSelect: (id: string) => void;
  onMoveSegment: (fromIdx: number, toIdx: number) => void;
  onRemoveSegment: (id: string) => void;
  formatTime: (secs: number) => string;
}

// Source colors for visual distinction between multiple clips
const SOURCE_COLORS = [
  'from-indigo-600 via-purple-600 to-indigo-700',
  'from-emerald-600 via-teal-600 to-emerald-700',
  'from-rose-600 via-pink-600 to-rose-700',
  'from-amber-600 via-orange-600 to-amber-700',
  'from-cyan-600 via-sky-600 to-cyan-700',
];

export function VideoTrackLane({
  segments,
  sources,
  totalSequenceDuration,
  selectedMedia,
  onSelect,
  onMoveSegment,
  onRemoveSegment,
  formatTime,
}: VideoTrackLaneProps) {
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);

  const handleDragStart = (idx: number, e: React.DragEvent) => {
    setDraggedIdx(idx);
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();

  const handleDrop = (targetIdx: number, e: React.DragEvent) => {
    e.preventDefault();
    if (draggedIdx === null) return;
    onMoveSegment(draggedIdx, targetIdx);
    setDraggedIdx(null);
  };

  // Build a quick source index lookup for colors
  const sourceColorMap: Record<string, number> = {};
  sources.forEach((s, i) => { sourceColorMap[s.id] = i % SOURCE_COLORS.length; });

  return (
    <div className="bg-white/90 dark:bg-slate-900/90 rounded-xl border border-slate-200 dark:border-slate-800 p-2 min-h-[64px] flex items-center relative overflow-hidden">
      <div className="mr-3 w-48 flex-shrink-0 space-y-0.5">
        <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider block">
          Track 1 (Video)
        </span>
        <span className="text-[9px] text-slate-500 block">Click clip to select • Drag to reorder</span>
      </div>

      <div className="flex-1 flex gap-1 items-center h-12 overflow-x-auto">
        {segments.length === 0 && (
          <span className="text-[10px] text-slate-600 italic">No clips — upload a video above</span>
        )}
        {segments.map((seg, idx) => {
          const segDur = Math.max(0.1, seg.endTime - seg.startTime);
          const widthPct = totalSequenceDuration > 0 ? (segDur / totalSequenceDuration) * 100 : 100;
          const isSelected = selectedMedia?.type === 'video' && selectedMedia.id === seg.id;
          const colorClass = SOURCE_COLORS[sourceColorMap[seg.sourceId] ?? 0];
          const source = sources.find(s => s.id === seg.sourceId);

          return (
            <div
              key={seg.id}
              draggable
              onClick={(e) => { e.stopPropagation(); onSelect(seg.id); }}
              onDragStart={(e) => handleDragStart(idx, e)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(idx, e)}
              style={{ width: `${Math.max(4, widthPct)}%`, minWidth: '60px' }}
              className={`h-11 rounded-lg bg-gradient-to-r ${colorClass} p-1.5 text-white flex items-center justify-between text-xs font-bold shadow-md border border-white/20 cursor-grab active:cursor-grabbing transition-all hover:brightness-110 flex-shrink-0 ${
                isSelected ? 'ring-2 ring-cyan-400 border-cyan-400 shadow-cyan-500/50' : ''
              } ${draggedIdx === idx ? 'opacity-40 ring-2 ring-yellow-400' : ''}`}
            >
              <div className="flex items-center gap-1 truncate min-w-0">
                <GripVertical className="w-3 h-3 opacity-60 flex-shrink-0" />
                <div className="truncate min-w-0">
                  <div className="text-[9px] opacity-70 truncate">{source?.name ?? 'Clip'}</div>
                  <div className="text-[10px] font-bold">{formatTime(segDur)}</div>
                </div>
              </div>

              <div className="flex items-center gap-0.5 flex-shrink-0 ml-1">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onMoveSegment(idx, idx - 1); }}
                  disabled={idx === 0}
                  className="p-0.5 hover:bg-black/40 rounded disabled:opacity-20"
                >
                  <ChevronLeft className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onMoveSegment(idx, idx + 1); }}
                  disabled={idx === segments.length - 1}
                  className="p-0.5 hover:bg-black/40 rounded disabled:opacity-20"
                >
                  <ChevronRight className="w-3 h-3" />
                </button>
                {segments.length > 1 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRemoveSegment(seg.id); }}
                    className="p-0.5 hover:bg-red-600/70 rounded text-red-200 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
